/**
 * How to read a stored pricing row's `price` — client-side mirror of the backend's
 * app/utils/pricing.util.js. The two MUST stay in step: the backend prices OTA
 * pages, listing cards and PDFs, the client prices the booking sidebar, the cart
 * and the checkout, and the checkout total is what actually gets charged. If they
 * disagree, a customer is billed something other than what they were shown.
 *
 * SAFETY: flat group pricing is opt-in through ONE explicit marker. An unknown
 * priceType, a missing one, or a legacy row (the old `tourPricings` table has no
 * such column) all fall back to per-person — today's behaviour. A price can never
 * become a flat total by accident.
 *
 * HEADCOUNT: always pass the same count the pricing row was matched on, which
 * across this codebase is `adults + children` (infants are free and never priced).
 * Passing a different number here than the one used to pick the row is how a group
 * ends up paying a tier it does not belong to.
 */

export type PricingRowLike = {
  price?: number | string | null;
  priceType?: string | null;
} | null | undefined;

/** The only priceType meaning "this price is the total for the whole group". */
export const FLAT_GROUP_PRICE_TYPE = 'perBooking';

export const isFlatGroupPrice = (priceType?: string | null): boolean =>
  priceType === FLAT_GROUP_PRICE_TYPE;

// Number(null) is 0 and Number.isFinite(0) is true, so a null/absent price would
// silently read as "free" instead of "no price here" — and the cart's fallback to
// the stored it.price would never fire.
const toPrice = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toHeadcount = (people: unknown): number => {
  const n = Number(people);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

/**
 * Per-person price to DISPLAY for a group of `people`.
 * Per-person row: the stored price. Flat row: the total split across the group.
 * Returns null when the row carries no usable price.
 */
export const perPersonPriceFor = (pricing: PricingRowLike, people: number): number | null => {
  const price = toPrice(pricing?.price);
  if (price == null) return null;
  if (!isFlatGroupPrice(pricing?.priceType)) return price;
  return price / toHeadcount(people);
};

/**
 * Total to CHARGE for a group of `people`.
 *
 * Deliberately not `perPersonPriceFor() * people`: dividing then multiplying back
 * is what turns a 300 € group price into 300,02 € once anything rounds to cents in
 * between. For a flat row the stored total is returned untouched, so the amount is
 * exact by construction. Use this for every subtotal; use perPersonPriceFor only
 * for the "/ pers." figure next to it.
 */
export const lineTotalFor = (pricing: PricingRowLike, people: number): number | null => {
  const price = toPrice(pricing?.price);
  if (price == null) return null;
  if (isFlatGroupPrice(pricing?.priceType)) return price;
  return price * toHeadcount(people);
};
